import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { queryClient } from '@/queries';
import {
  getHrSystemConflictItemRemover,
  getSchedulerShiftItemRemover,
  getSchedulerShiftItemUpdater,
  sScheduleQueries,
  sLiScheduleQueries,
  sHtLuScheduleQueries,
} from '@/queries/resource/schedule';
import {
  DateRangeParams,
  ResetShiftTypeRequestBody,
  SetSShiftTypeRequestBody,
  SetFixationTypeRequestBody,
  SRequirementsInputsSettings,
  SResourceRulesSettings,
  PlanningProcess,
  SResolveHrSystemConflict,
  SShift,
} from '@/api/apis/resources/schedule';

type SOrganQueriesType = typeof sLiScheduleQueries | typeof sHtLuScheduleQueries;
type SetSShiftTypeFetcher = typeof api.schedule.s.setSLiShiftType | typeof api.schedule.s.setSHtLuShiftType;
type ResetSShiftTypeFetcher = typeof api.schedule.s.resetSLiShiftType | typeof api.schedule.s.resetSHtLuShiftType;

const createHandleSSchedulePlanningQueryUpdate =
  (sOrganQueries: SOrganQueriesType) => (startDayOfMonth: Date, planningData: PlanningProcess) => {
    queryClient.invalidateQueries(sOrganQueries.schedule(startDayOfMonth));
    queryClient.setQueryData(sScheduleQueries.planningProcess(planningData.id).queryKey, planningData);
  };
const handleLiSSchedulePlanningQueryUpdate = createHandleSSchedulePlanningQueryUpdate(sLiScheduleQueries);
const handleHtLuSSchedulePlanningQueryUpdate = createHandleSSchedulePlanningQueryUpdate(sHtLuScheduleQueries);

const createUseSScheduleSetShiftTypeOptimisticMutation =
  (sOrganQueries: SOrganQueriesType, setSShiftTypeFetcher: SetSShiftTypeFetcher) =>
  (dateRange: DateRangeParams, processId?: string | null, isPlanned?: boolean) => {
    const { queryKey } = sOrganQueries.shifts(dateRange, isPlanned ? processId : undefined);

    return useMutation({
      mutationFn: (body: SetSShiftTypeRequestBody) => setSShiftTypeFetcher(body),
      onMutate: (body) => {
        const prevSnapshot = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater(body));

        return prevSnapshot;
      },
      onSuccess: (data, variables) => {
        queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater({ ...variables, ...data }));
        queryClient.invalidateQueries(sScheduleQueries.validation(variables.scheduleId));
      },
      onError: (_e, _p, context) => {
        queryClient.setQueryData(queryKey, context);
      },
    });
  };
export const useLiSScheduleSetShiftTypeOptimisticMutation = createUseSScheduleSetShiftTypeOptimisticMutation(
  sLiScheduleQueries,
  api.schedule.s.setSLiShiftType,
);
export const useHtLuSScheduleSetShiftTypeOptimisticMutation = createUseSScheduleSetShiftTypeOptimisticMutation(
  sHtLuScheduleQueries,
  api.schedule.s.setSHtLuShiftType,
);

const createUseSScheduleResetShiftTypeOptimisticMutation =
  (sOrganQueries: SOrganQueriesType, resetSShiftTypeFetcher: ResetSShiftTypeFetcher) =>
  (dateRange: DateRangeParams, processId?: string | null, isPlanned?: boolean) => {
    const { queryKey } = sOrganQueries.shifts(dateRange, isPlanned ? processId : undefined);

    return useMutation({
      mutationFn: (body: ResetShiftTypeRequestBody) => resetSShiftTypeFetcher(body),
      onMutate: (body) => {
        const prevSnapshot = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, getSchedulerShiftItemRemover(body));

        return prevSnapshot;
      },
      onSuccess: (_d, variables) => {
        queryClient.invalidateQueries(sScheduleQueries.validation(variables.scheduleId));
      },
      onError: (_e, _p, context) => {
        queryClient.setQueryData(queryKey, context);
      },
    });
  };
export const useLiSScheduleResetShiftTypeOptimisticMutation = createUseSScheduleResetShiftTypeOptimisticMutation(
  sLiScheduleQueries,
  api.schedule.s.resetSLiShiftType,
);
export const useHtLuSScheduleResetShiftTypeOptimisticMutation = createUseSScheduleResetShiftTypeOptimisticMutation(
  sHtLuScheduleQueries,
  api.schedule.s.resetSHtLuShiftType,
);

export const useLiSCreatePlanningMutation = () => {
  return useMutation({
    mutationFn: (startDayOfMonth: Date) => api.schedule.s.createSLiPlanningProcess(startDayOfMonth),
    onSuccess: (data, startDayOfMonth) => handleLiSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};
export const useHtLuSCreatePlanningMutation = () => {
  return useMutation({
    mutationFn: (startDayOfMonth: Date) => api.schedule.s.createSHtLuPlanningProcess(startDayOfMonth),
    onSuccess: (data, startDayOfMonth) => handleHtLuSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};

export const useLiSRegeneratePlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.s.regenerateSPlanningProcess(processId),
    onSuccess: (data) => handleLiSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};
export const useHtLuSRegeneratePlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.s.regenerateSPlanningProcess(processId),
    onSuccess: (data) => handleHtLuSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};

export const useLiSCancelPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.s.cancelSPlanningProcess(processId),
    onSuccess: (data) => handleLiSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};
export const useHtLuSCancelPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.s.cancelSPlanningProcess(processId),
    onSuccess: (data) => handleHtLuSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};

export const useLiSPublishPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, scheduleId }: { processId: string; scheduleId: string }) =>
      api.schedule.s.publishSPlanningProcess(processId, scheduleId),
    onSuccess: (data) => handleLiSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};
export const useHtLuSPublishPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, scheduleId }: { processId: string; scheduleId: string }) =>
      api.schedule.s.publishSPlanningProcess(processId, scheduleId),
    onSuccess: (data) => handleHtLuSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};

export const useLiSCancelGenerationPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, jobId }: { processId: string; jobId: string }) =>
      api.schedule.s.cancelGenerationSPlanningProcess(processId, jobId),
    onSuccess: (data) => handleLiSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};
export const useHtLuSCancelGenerationPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, jobId }: { processId: string; jobId: string }) =>
      api.schedule.s.cancelGenerationSPlanningProcess(processId, jobId),
    onSuccess: (data) => handleHtLuSSchedulePlanningQueryUpdate(startDayOfMonth, data),
  });
};

export const useLiSSetScheduleSettingsRulesMutation = () => {
  return useMutation({
    mutationFn: (body: SResourceRulesSettings) => api.schedule.s.setSLiScheduleRules(body),
    meta: { invalidates: [sLiScheduleQueries.rules().queryKey] },
    onSuccess: (data) => queryClient.setQueryData(sLiScheduleQueries.rules().queryKey, data),
  });
};
export const useHtLuSSetScheduleSettingsRulesMutation = () => {
  return useMutation({
    mutationFn: (body: SResourceRulesSettings) => api.schedule.s.setSHtLuScheduleRules(body),
    meta: { invalidates: [sHtLuScheduleQueries.rules().queryKey] },
    onSuccess: (data) => queryClient.setQueryData(sHtLuScheduleQueries.rules().queryKey, data),
  });
};

export const useLiSSetScheduleSettingsInputsMutation = () => {
  return useMutation({
    mutationFn: (body: SRequirementsInputsSettings) => api.schedule.s.setSLiScheduleInputs(body),
    meta: { invalidates: [sLiScheduleQueries.inputs().queryKey] },
    onSuccess: (data) => queryClient.setQueryData(sLiScheduleQueries.inputs().queryKey, data),
  });
};
export const useHtLuSSetScheduleSettingsInputsMutation = () => {
  return useMutation({
    mutationFn: (body: SRequirementsInputsSettings) => api.schedule.s.setSHtLuScheduleInputs(body),
    meta: { invalidates: [sHtLuScheduleQueries.inputs().queryKey] },
    onSuccess: (data) => queryClient.setQueryData(sHtLuScheduleQueries.inputs().queryKey, data),
  });
};

const createUseSScheduleSyncWorkdayMutation =
  (sOrganQueries: SOrganQueriesType) =>
  (startDayOfMonth: Date, dateRange: DateRangeParams, processId?: string | null, isPlanned?: boolean) => {
    return useMutation({
      mutationFn: (scheduleId: string) => api.schedule.s.sScheduleRefreshHr(scheduleId),
      onSuccess: (_, scheduleId) => {
        const planningProcessId = isPlanned ? processId : undefined;
        queryClient.invalidateQueries({ queryKey: sOrganQueries.shifts(dateRange, planningProcessId).queryKey });
        queryClient.invalidateQueries({ queryKey: sOrganQueries.schedule(startDayOfMonth).queryKey });
        queryClient.invalidateQueries({ queryKey: sScheduleQueries.planningProcess(processId).queryKey });
        queryClient.invalidateQueries({ queryKey: sScheduleQueries.hrSystemConflicts(scheduleId).queryKey });
      },
    });
  };
export const useLiSScheduleSyncWorkdayMutation = createUseSScheduleSyncWorkdayMutation(sLiScheduleQueries);
export const useHtLuSScheduleSyncWorkdayMutation = createUseSScheduleSyncWorkdayMutation(sHtLuScheduleQueries);

const createUseSHrSystemConflictResolveMutation =
  (sOrganQueries: SOrganQueriesType) => (dateRange: DateRangeParams, scheduleId?: string) => {
    return useMutation({
      mutationFn: (payload: SResolveHrSystemConflict) => api.schedule.s.resolveSScheduleHrSystemConflict(payload),
      onSuccess: (data, variables) => {
        queryClient.setQueryData(
          sScheduleQueries.hrSystemConflicts(scheduleId).queryKey,
          getHrSystemConflictItemRemover(variables.conflictId),
        );
        queryClient.setQueryData(sOrganQueries.shifts(dateRange).queryKey, getSchedulerShiftItemUpdater(data));
      },
    });
  };
export const useLiSHrSystemConflictResolveMutation = createUseSHrSystemConflictResolveMutation(sLiScheduleQueries);
export const useHtLuSHrSystemConflictResolveMutation = createUseSHrSystemConflictResolveMutation(sHtLuScheduleQueries);

const createUseSSetFixationTypeOptimisticMutation =
  (sOrganQueries: SOrganQueriesType) =>
  (dateRange: DateRangeParams, processId?: string | null, isPlanned?: boolean) => {
    const { queryKey } = sOrganQueries.shifts(dateRange, isPlanned ? processId : undefined);

    return useMutation({
      mutationFn: (body: SetFixationTypeRequestBody) => api.schedule.s.setSFixationType(body),
      onMutate: (body) => {
        const prevSnapshot = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(
          queryKey,
          getSchedulerShiftItemUpdater({ ...body, fixationType: body.fixationType, isEditable: true } as SShift),
        );
        return prevSnapshot;
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(sScheduleQueries.validation(variables.scheduleId));
      },
      onError: (_e, _p, context) => {
        queryClient.setQueryData(queryKey, context);
      },
    });
  };
export const useLiSSetFixationTypeOptimisticMutation = createUseSSetFixationTypeOptimisticMutation(sLiScheduleQueries);
export const useHtLuSSetFixationTypeOptimisticMutation =
  createUseSSetFixationTypeOptimisticMutation(sHtLuScheduleQueries);
